import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  ViewEncapsulation,
} from '@angular/core';
import {
  DeclarativeMapCanvasComponent,
  DeclarativeMapMarkerComponent,
  DeclarativeMapMarkerTemplateDirective,
} from '@cue/map/elements';
import { NavigationGeodataBM } from '@cue/data';
import { cueQrCode, cueStart, IconComponent } from '@cue/ui/icons';
import { translation } from '@cue/translations';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { QrCodeShowOnFloorplanEnum, ShowOnFloorplanEnum } from '@cue/api';
import { NAVIGATION_SETTINGS } from '@cue/navigation-settings';
import { ImageDimensionsPipe, LocalizedStringPipe } from '@cue/utilities';
import { MAP_ACTIVE_Z_INDEX_OFFSET } from '@cue/map/core';

@Component({
  selector: 'cue-map-poi',
  imports: [
    DeclarativeMapMarkerComponent,
    DeclarativeMapMarkerTemplateDirective,
    IconComponent,
    NgTemplateOutlet,
    NgStyle,
    LocalizedStringPipe,
    ImageDimensionsPipe,
  ],
  templateUrl: './map-poi.component.html',
  styleUrl: './map-poi.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class MapPoiComponent {
  protected readonly activeZIndexOffset = MAP_ACTIVE_Z_INDEX_OFFSET;
  ICONS = {
    cueStart,
    cueQrCode,
  };

  generatedDomId = Math.floor(Math.random() * (999999 - 100000)) + 100000;
  navigationSettings = inject(NAVIGATION_SETTINGS);
  canvas = inject(DeclarativeMapCanvasComponent);

  deploymentId = input<string>();

  zoom = computed(() => {
    return this.canvas.zoom();
  });
  click = output();
  poi = input<NavigationGeodataBM>();

  kioskId = input<string>();

  focused = input<boolean, boolean | 'true' | 'false'>(false, {
    transform: booleanAttribute,
  });

  sizeCoefficient = computed(() => {
    return this.poi()?.poiSize ?? 1;
  });

  focusCoefficient = computed(() => {
    switch (this.sizeCoefficient()) {
      case 0.5:
        return 2;
      case 1:
        return 1.25;
      case 2:
        return 1.25;
      default:
        return 1.25;
    }
  });

  customResizeBasedOnZoomEnabled = computed(() => {
    return this.poi()?.zoomPoi;
  });

  scale = computed(() => {
    const zoomValue = Number.isFinite(this.zoom()) ? this.zoom() : 1;
    let nonZoomedScale = 1;
    if (this.customResizeBasedOnZoomEnabled()) {
      if (zoomValue >= 0.5) {
        nonZoomedScale = this.sizeCoefficient() * zoomValue;
      } else {
        nonZoomedScale = this.sizeCoefficient() * 0.5;
      }
    } else {
      nonZoomedScale = this.sizeCoefficient();
    }
    const focusedScale = this.focused() ? this.focusCoefficient() : 1;
    return nonZoomedScale * focusedScale;
  });

  scaleDEP = computed(() => {
    const zoomValue = Number.isFinite(this.zoom()) ? this.zoom() : 1;
    let nonZoomedScale = 1;
    if (zoomValue >= 0.5) {
      nonZoomedScale = this.sizeCoefficient() * zoomValue;
    } else {
      nonZoomedScale = this.sizeCoefficient() * 0.5;
    }
    const focusedScale = this.focused() ? this.focusCoefficient() : 1;
    return nonZoomedScale * focusedScale;
  });

  onHostClick(ev: MouseEvent) {
    ev.stopPropagation();
    this.click.emit();
  }

  private convertRgbaToRgb(color?: string, alpha = 1) {
    if (!color || color?.startsWith('rgb(')) {
      return color;
    }
    // Extract first 3 numbers from rgba
    const [r, g, b] = color.match(/\d+/g).map(Number);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private getTextColorBasedOnBg(color?: string): string {
    if (!color) return '#212529';
    const [r, g, b] = color.match(/\d+/g).map(Number);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 128 ? '#212529' : '#ffffff';
  }

  customRgb = computed(() => {
    return this.convertRgbaToRgb(this.poi()?.qrCategoryColor);
  });

  customRgba = computed(() => {
    return this.poi()?.qrCategoryColor;
  });

  customRgbaSaturated = computed(() => {
    return this.convertRgbaToRgb(this.poi()?.qrCategoryColor, 0.8);
  });

  automaticTextColor = computed(() => {
    if (this.poi()?.qrCategoryColor) {
      return this.getTextColorBasedOnBg(this.poi()?.qrCategoryColor);
    }
    return '';
  });

  isActiveItem = computed(() => {
    return this.focused();
  });

  isDeploymentMode = computed(() => {
    return (
      this.navigationSettings.deploymentSettings?.enabled &&
      this.navigationSettings.deploymentSettings.id === this.deploymentId()
    );
  });

  qrCodeShowSettingsForDeploymentMode = computed(() => {
    return this.navigationSettings.deploymentSettings?.qrCodeShowOnFloorplanId;
  });

  protected readonly translation = translation;
  protected readonly qrCodeShowOnFloorplanEnum = QrCodeShowOnFloorplanEnum;
  protected readonly showOnFloorplanEnum = ShowOnFloorplanEnum;
}
